import React, { useState, useEffect } from 'react';
import { Home, Wrench, Smartphone, Sparkles, Phone, Mail, MapPin, Menu, X, ChevronRight, Check, Clock, Shield, Star, Users, Heart, ArrowRight } from 'lucide-react';

const TidyTurnWebsite = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const TidyTurnLogo = () => (
    <svg viewBox="0 0 40 40" className="w-10 h-10 mr-2">
      <g fill="currentColor">
        {/* House outline */}
        <path d="M20 6 L5 18 L5 32 Q5 34 7 34 L33 34 Q35 34 35 32 L35 18 Z" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <path d="M20 6 L35 18" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        <path d="M5 18 L20 6" fill="none" stroke="currentColor" strokeWidth="2.5"/>
        
        {/* Broom */}
        <rect x="22" y="12" width="3" height="12" rx="1.5" transform="rotate(35 23.5 18)" />
        <path d="M18 20 Q18 18 20 18 L24 18 Q26 18 26 20 L26 24 Q26 26 25 26 L19 26 Q18 26 18 24 Z" transform="rotate(35 22 22)"/>
        
        {/* Sparkles */}
        <path d="M12 12 L13 10 L14 12 L16 13 L14 14 L13 16 L12 14 L10 13 Z" transform="scale(0.6) translate(8 5)"/>
        <path d="M15 8 L15.5 7 L16 8 L17 8.5 L16 9 L15.5 10 L15 9 L14 8.5 Z" transform="scale(0.5) translate(14 8)"/>
      </g>
    </svg>
  );

  const Navigation = () => (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center">
            <div className="flex items-center text-teal-600">
              <TidyTurnLogo />
              <h1 className="text-2xl font-bold">TidyTurn</h1>
            </div>
          </div>
          
          <div className="hidden md:flex space-x-8">
            {['home', 'services', 'about', 'contact'].map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`capitalize transition-colors ${currentPage === page ? 'text-teal-600 font-semibold' : 'text-gray-700 hover:text-teal-600'}`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage('quote')}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors"
            >
              Get Quote
            </button>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {['home', 'services', 'about', 'contact', 'quote'].map((page) => (
              <button
                key={page}
                onClick={() => {
                  setCurrentPage(page);
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 capitalize ${currentPage === page ? 'text-teal-600 font-semibold' : 'text-gray-700'}`}
              >
                {page === 'quote' ? 'Get Quote' : page}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );

  const HomePage = () => (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-blue-50 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Your Complete Property Care Solution
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                From pristine short-term rental cleanings to expert home repairs and authorized tech services - we've got you covered.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setCurrentPage('services')}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-all transform hover:scale-105 flex items-center justify-center"
                >
                  Explore Services <ChevronRight className="ml-2" />
                </button>
                <button
                  onClick={() => setCurrentPage('contact')}
                  className="border-2 border-teal-600 text-teal-600 px-6 py-3 rounded-lg hover:bg-teal-50 transition-all"
                >
                  Contact Us
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-teal-100 rounded-lg p-6 text-center">
                    <Sparkles className="w-12 h-12 text-teal-600 mx-auto mb-2" />
                    <p className="font-semibold">Cleaning</p>
                  </div>
                  <div className="bg-blue-100 rounded-lg p-6 text-center">
                    <Wrench className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                    <p className="font-semibold">Repairs</p>
                  </div>
                  <div className="bg-purple-100 rounded-lg p-6 text-center">
                    <Smartphone className="w-12 h-12 text-purple-600 mx-auto mb-2" />
                    <p className="font-semibold">Tech</p>
                  </div>
                  <div className="bg-orange-100 rounded-lg p-6 text-center">
                    <Home className="w-12 h-12 text-orange-600 mx-auto mb-2" />
                    <p className="font-semibold">Property</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12">Our Core Services</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow">
              <Sparkles className="w-12 h-12 text-teal-600 mb-4" />
              <h4 className="text-xl font-bold mb-3">Short-Term Rental Cleaning</h4>
              <p className="text-gray-600 mb-4">Professional deep cleaning services tailored for Airbnb and vacation rental properties.</p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  5-star standard cleaning
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Linen service available
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Same-day turnaround
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow">
              <Wrench className="w-12 h-12 text-blue-600 mb-4" />
              <h4 className="text-xl font-bold mb-3">Home Repairs & Maintenance</h4>
              <p className="text-gray-600 mb-4">Expert repair services for all your home maintenance needs.</p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Plumbing & HVAC
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Electrical & Carpentry
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Floor & Carpet Care
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-shadow">
              <Smartphone className="w-12 h-12 text-purple-600 mb-4" />
              <h4 className="text-xl font-bold mb-3">Authorized Tech Repair</h4>
              <p className="text-gray-600 mb-4">Certified repair services for major tech brands.</p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Samsung Authorized
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Apple Certified
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Warranty Preserved
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12">Why Choose TidyTurn?</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-teal-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Clock className="w-10 h-10 text-teal-600" />
              </div>
              <h4 className="font-bold mb-2">24/7 Availability</h4>
              <p className="text-gray-600">Emergency services when you need them most</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-blue-600" />
              </div>
              <h4 className="font-bold mb-2">Licensed & Insured</h4>
              <p className="text-gray-600">Fully certified professionals you can trust</p>
            </div>
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Star className="w-10 h-10 text-purple-600" />
              </div>
              <h4 className="font-bold mb-2">5-Star Service</h4>
              <p className="text-gray-600">Consistently rated excellent by our clients</p>
            </div>
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-orange-600" />
              </div>
              <h4 className="font-bold mb-2">Expert Team</h4>
              <p className="text-gray-600">Skilled technicians for every service</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-3xl font-bold text-center mb-12">What Our Clients Say</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4">"TidyTurn transformed my Airbnb business! Their cleaning service is impeccable and guests always comment on how spotless everything is."</p>
              <p className="font-semibold">- Sarah M., Property Owner</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4">"Fast, reliable, and professional. They fixed our HVAC issue same day and the price was very reasonable. Highly recommend!"</p>
              <p className="font-semibold">- Michael T., Homeowner</p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4">"Got my Samsung phone repaired here. They're authorized, so my warranty stayed intact. Quick turnaround too!"</p>
              <p className="font-semibold">- Jessica L., Tech Customer</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 className="text-3xl font-bold mb-4">Ready to Experience Premium Service?</h3>
          <p className="text-xl mb-8">Get a free quote today and see why Atlanta trusts TidyTurn</p>
          <button
            onClick={() => setCurrentPage('quote')}
            className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
          >
            Get Your Free Quote <ArrowRight className="inline ml-2" />
          </button>
        </div>
      </section>
    </div>
  );

  const ServicesPage = () => (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-4">Our Services</h2>
        <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
          Comprehensive property care solutions for residential and commercial clients
        </p>

        {/* Cleaning Services */}
        <div className="mb-16">
          <div className="flex items-center mb-8">
            <Sparkles className="w-8 h-8 text-teal-600 mr-3" />
            <h3 className="text-2xl font-bold">Cleaning Services</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h4 className="text-xl font-bold mb-4">Short-Term Rental Cleaning</h4>
              <p className="text-gray-600 mb-4">Specialized cleaning for Airbnb, VRBO, and vacation rentals</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Complete property sanitization between guests</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Linen washing and replacement service</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Restocking of amenities and supplies</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Photo-verified completion reports</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Flexible scheduling and emergency cleanings</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h4 className="text-xl font-bold mb-4">Deep Cleaning Services</h4>
              <p className="text-gray-600 mb-4">Thorough cleaning for homes and businesses</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Move-in/move-out cleaning</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Post-construction cleanup</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Spring cleaning packages</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Office and commercial spaces</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Eco-friendly cleaning options</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Home Repair Services */}
        <div className="mb-16">
          <div className="flex items-center mb-8">
            <Wrench className="w-8 h-8 text-blue-600 mr-3" />
            <h3 className="text-2xl font-bold">Home Repair & Maintenance</h3>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">Plumbing</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Leak repairs</li>
                <li>• Fixture installation</li>
                <li>• Drain cleaning</li>
                <li>• Water heater service</li>
                <li>• Emergency plumbing</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">HVAC</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• AC repair & maintenance</li>
                <li>• Heating system service</li>
                <li>• Filter replacement</li>
                <li>• Thermostat installation</li>
                <li>• Air quality solutions</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">Electrical</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Outlet & switch repair</li>
                <li>• Lighting installation</li>
                <li>• Circuit breaker service</li>
                <li>• Safety inspections</li>
                <li>• Smart home setup</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">Flooring</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Hardwood repair</li>
                <li>• Tile installation</li>
                <li>• Carpet stretching</li>
                <li>• Vinyl/laminate repair</li>
                <li>• Floor refinishing</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">Carpet Cleaning</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Deep steam cleaning</li>
                <li>• Stain removal</li>
                <li>• Pet odor treatment</li>
                <li>• Upholstery cleaning</li>
                <li>• Area rug service</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h4 className="text-lg font-bold mb-3">Handyman</h4>
              <ul className="space-y-2 text-gray-600">
                <li>• Furniture assembly</li>
                <li>• Drywall repair</li>
                <li>• Painting touch-ups</li>
                <li>• Shelving installation</li>
                <li>• General maintenance</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Tech Repair Services */}
        <div>
          <div className="flex items-center mb-8">
            <Smartphone className="w-8 h-8 text-purple-600 mr-3" />
            <h3 className="text-2xl font-bold">Authorized Tech Repair</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h4 className="text-xl font-bold mb-4">Samsung Authorized Service</h4>
              <p className="text-gray-600 mb-4">Official Samsung repair center with certified technicians</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Galaxy phone screen replacement</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Battery replacement service</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Tablet and wearable repairs</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Software troubleshooting</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Warranty-preserved repairs</span>
                </li>
              </ul>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h4 className="text-xl font-bold mb-4">Apple Certified Repairs</h4>
              <p className="text-gray-600 mb-4">Expert Apple device repair with genuine parts</p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>iPhone screen and battery service</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>MacBook hardware repairs</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>iPad screen replacement</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>Data recovery services</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-green-500 mr-2 mt-0.5" />
                  <span>AppleCare+ support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className="pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-bold text-center mb-12">About TidyTurn</h2>
        
        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h3 className="text-2xl font-bold mb-4">Our Story</h3>
            <p className="text-gray-600 mb-4">
              Founded in Atlanta, TidyTurn began with a simple mission: to provide comprehensive property care services under one trusted roof. We recognized that property owners and managers were juggling multiple vendors for cleaning, repairs, and maintenance - and we knew there was a better way.
            </p>
            <p className="text-gray-600 mb-4">
              Today, we're proud to be Atlanta's premier one-stop solution for short-term rental cleaning, home repairs, and authorized tech services. Our integrated approach saves our clients time, money, and the hassle of coordinating multiple service providers.
            </p>
            <p className="text-gray-600">
              From a small team of dedicated professionals, we've grown to serve hundreds of properties across the Atlanta metro area, maintaining our commitment to excellence, reliability, and customer satisfaction every step of the way.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-teal-50 rounded-xl p-6 text-center">
              <h4 className="text-3xl font-bold text-teal-600 mb-2">500+</h4>
              <p className="text-gray-700">Properties Serviced</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 text-center">
              <h4 className="text-3xl font-bold text-blue-600 mb-2">50+</h4>
              <p className="text-gray-700">Expert Technicians</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 text-center">
              <h4 className="text-3xl font-bold text-purple-600 mb-2">4.9★</h4>
              <p className="text-gray-700">Customer Rating</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h3 className="text-2xl font-bold mb-4">Our Values</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-bold text-lg mb-2 flex items-center">
                  <Heart className="w-5 h-5 text-red-500 mr-2" />
                  Excellence
                </h4>
                <p className="text-gray-600">We don't just meet standards - we set them. Every service we provide is executed with meticulous attention to detail.</p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 flex items-center">
                  <Shield className="w-5 h-5 text-blue-500 mr-2" />
                  Trust
                </h4>
                <p className="text-gray-600">Licensed, insured, and background-checked professionals you can welcome into your property with confidence.</p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 flex items-center">
                  <Clock className="w-5 h-5 text-green-500 mr-2" />
                  Reliability
                </h4>
                <p className="text-gray-600">When we say we'll be there, we're there. Your time is valuable, and we respect that with punctual, efficient service.</p>
              </div>
              <div>
                <h4 className="font-bold text-lg mb-2 flex items-center">
                  <Users className="w-5 h-5 text-purple-500 mr-2" />
                  Community
                </h4>
                <p className="text-gray-600">We're not just a service provider - we're your neighbors, committed to making Atlanta properties shine.</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-xl p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Experience the TidyTurn Difference?</h3>
            <p className="mb-6">Join hundreds of satisfied property owners who trust us with their valuable investments.</p>
            <button
              onClick={() => setCurrentPage('contact')}
              className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all"
            >
              Get Started Today
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ContactPage = () => {
    const [contactForm, setContactForm] = useState({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
    const [submitted, setSubmitted] = useState(false);

    const handleContactSubmit = () => {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 3000);
    };

    return (
      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Contact Us</h2>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-6">Get in Touch</h3>
              <p className="text-gray-600 mb-8">
                Have questions about our services? Need to schedule a cleaning or repair? We're here to help! Reach out to us using any of the methods below.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-teal-100 rounded-full p-3 mr-4">
                    <Phone className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Phone</h4>
                    <p className="text-gray-600">305-814-0416</p>
                    <p className="text-sm text-gray-500">Available 24/7 for emergencies</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-full p-3 mr-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Email</h4>
                    <p className="text-gray-600">support@tidyturn.io</p>
                    <p className="text-sm text-gray-500">We respond within 2 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-purple-100 rounded-full p-3 mr-4">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Office</h4>
                    <p className="text-gray-600">1445 Woodmont Ln NW #942</p>
                    <p className="text-gray-600">Atlanta, GA 30318</p>
                    <p className="text-sm text-gray-500">Mon-Fri 8am-6pm, Sat 9am-4pm</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gray-50 rounded-xl p-6">
                <h4 className="font-bold mb-3">Service Areas</h4>
                <p className="text-gray-600">We proudly serve the entire Atlanta metro area including:</p>
                <p className="text-sm text-gray-500 mt-2">
                  Buckhead • Midtown • Downtown • Decatur • Sandy Springs • Alpharetta • Marietta • Roswell • Dunwoody • And more!
                </p>
              </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-xl font-bold mb-6">Send Us a Message</h3>
              {submitted ? (
                <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded">
                  <p className="font-bold">Thank you!</p>
                  <p>We've received your message and will get back to you soon.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Your name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="your@email.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Service Needed</label>
                    <select 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      value={contactForm.service}
                      onChange={(e) => setContactForm({...contactForm, service: e.target.value})}
                    >
                      <option value="">Select a service</option>
                      <option>Short-Term Rental Cleaning</option>
                      <option>Home Repairs</option>
                      <option>HVAC Service</option>
                      <option>Plumbing</option>
                      <option>Electrical</option>
                      <option>Tech Repair</option>
                      <option>Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea
                      rows={4}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Tell us how we can help..."
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    ></textarea>
                  </div>
                  
                  <button
                    onClick={handleContactSubmit}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                  >
                    Send Message
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const QuotePage = () => {
    const [quoteForm, setQuoteForm] = useState({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      propertyType: '',
      services: [],
      frequency: 'One-time service',
      address: '',
      city: '',
      state: 'GA',
      zip: '',
      details: ''
    });
    const [submitted, setSubmitted] = useState(false);

    const handleServiceToggle = (service) => {
      setQuoteForm(prev => ({
        ...prev,
        services: prev.services.includes(service)
          ? prev.services.filter(s => s !== service)
          : [...prev.services, service]
      }));
    };

    const handleQuoteSubmit = () => {
      setSubmitted(true);
      window.scrollTo(0, 0);
    };

    if (submitted) {
      return (
        <div className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-green-50 rounded-xl p-12 text-center">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold mb-4">Quote Request Received!</h2>
              <p className="text-lg text-gray-700 mb-6">
                Thank you for your interest in TidyTurn services. Our team will review your request and contact you within 24 hours with a detailed quote.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setQuoteForm({
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: '',
                    propertyType: '',
                    services: [],
                    frequency: 'One-time service',
                    address: '',
                    city: '',
                    state: 'GA',
                    zip: '',
                    details: ''
                  });
                }}
                className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Submit Another Quote
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4">Get Your Free Quote</h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Tell us about your needs and we'll provide a detailed estimate within 24 hours
          </p>
          
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={quoteForm.firstName}
                    onChange={(e) => setQuoteForm({...quoteForm, firstName: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={quoteForm.lastName}
                    onChange={(e) => setQuoteForm({...quoteForm, lastName: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={quoteForm.email}
                    onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type *</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  value={quoteForm.propertyType}
                  onChange={(e) => setQuoteForm({...quoteForm, propertyType: e.target.value})}
                >
                  <option value="">Select property type</option>
                  <option>Single Family Home</option>
                  <option>Apartment/Condo</option>
                  <option>Townhouse</option>
                  <option>Commercial Property</option>
                  <option>Short-Term Rental</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Services Needed *</label>
                <div className="space-y-2">
                  {[
                    'Short-Term Rental Cleaning',
                    'Deep Cleaning',
                    'Plumbing Repair',
                    'HVAC Service',
                    'Electrical Work',
                    'Floor/Carpet Service',
                    'Handyman Services',
                    'Tech Repair'
                  ].map(service => (
                    <label key={service} className="flex items-center">
                      <input 
                        type="checkbox" 
                        className="mr-2 text-teal-600"
                        checked={quoteForm.services.includes(service)}
                        onChange={() => handleServiceToggle(service)}
                      />
                      <span>{service}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Frequency</label>
                <select 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  value={quoteForm.frequency}
                  onChange={(e) => setQuoteForm({...quoteForm, frequency: e.target.value})}
                >
                  <option>One-time service</option>
                  <option>Weekly</option>
                  <option>Bi-weekly</option>
                  <option>Monthly</option>
                  <option>As needed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Address</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Street address"
                  value={quoteForm.address}
                  onChange={(e) => setQuoteForm({...quoteForm, address: e.target.value})}
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="City"
                    value={quoteForm.city}
                    onChange={(e) => setQuoteForm({...quoteForm, city: e.target.value})}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="State"
                    value={quoteForm.state}
                    onChange={(e) => setQuoteForm({...quoteForm, state: e.target.value})}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="ZIP"
                    value={quoteForm.zip}
                    onChange={(e) => setQuoteForm({...quoteForm, zip: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="Please provide any additional information about your service needs..."
                  value={quoteForm.details}
                  onChange={(e) => setQuoteForm({...quoteForm, details: e.target.value})}
                ></textarea>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  <strong>What happens next?</strong> Once you submit this form, our team will review your needs and prepare a detailed quote. We'll contact you within 24 hours with pricing and available scheduling options.
                </p>
              </div>
              
              <button
                onClick={handleQuoteSubmit}
                className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors"
              >
                Submit Quote Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const Footer = () => (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center mb-4">
              <div className="text-teal-400">
                <TidyTurnLogo />
              </div>
              <h3 className="text-xl font-bold ml-2">TidyTurn</h3>
            </div>
            <p className="text-gray-400">Your complete property care solution in Atlanta.</p>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400">
              <li>
                <button onClick={() => setCurrentPage('services')} className="hover:text-white transition-colors">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => setCurrentPage('about')} className="hover:text-white transition-colors">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => setCurrentPage('contact')} className="hover:text-white transition-colors">
                  Contact
                </button>
              </li>
              <li>
                <button onClick={() => setCurrentPage('quote')} className="hover:text-white transition-colors">
                  Get Quote
                </button>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">Services</h4>
            <ul className="space-y-2 text-gray-400">
              <li>STR Cleaning</li>
              <li>Home Repairs</li>
              <li>Tech Repair</li>
              <li>Emergency Service</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-4">Contact Info</h4>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-center">
                <Phone className="w-4 h-4 mr-2" />
                305-814-0416
              </li>
              <li className="flex items-center">
                <Mail className="w-4 h-4 mr-2" />
                support@tidyturn.io
              </li>
              <li className="flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-1" />
                <span>
                  1445 Woodmont Ln NW #942<br />
                  Atlanta, GA 30318
                </span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
          <p>&copy; 2025 TidyTurn. All rights reserved. | Licensed & Insured</p>
        </div>
      </div>
    </footer>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'services' && <ServicesPage />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'contact' && <ContactPage />}
      {currentPage === 'quote' && <QuotePage />}
      <Footer />
    </div>
  );
};

export default TidyTurnWebsite;